import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LayoutDashboard, Shield } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantView } from '../../contexts/TenantViewContext';
import { supabase } from '@/integrations/supabase/client';

/**
 * Floating toggle shown at the bottom of the screen for admins, owners, and
 * concierges who are also tenants (attached to an apartment).
 * - On role routes (/admin, /owner, /concierge) → shows "Tenant View" button
 * - On user routes, only if they arrived via the toggle → shows role-appropriate back button
 * Hidden entirely if the logged-in user has no tenant apartment.
 */
const ViewToggle: React.FC = () => {
  const { isAdmin, isOwner, isConcierge, user } = useAuth();
  const { inTenantView, viewingApartmentId, enterTenantView, exitTenantView } = useTenantView();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasApartment, setHasApartment] = useState(false);

  const userIsAdmin = isAdmin();
  const userIsOwner = isOwner();
  const userIsConcierge = isConcierge();
  const hasRoleView = userIsAdmin || userIsOwner || userIsConcierge;

  const isOnRoleRoute =
    location.pathname.startsWith('/admin') ||
    location.pathname.startsWith('/superadmin') ||
    location.pathname.startsWith('/owner') ||
    location.pathname.startsWith('/concierge');
  const isOnUserRoute = location.pathname.startsWith('/user');

  // Role-appropriate back destination
  const roleRoute = userIsAdmin
    ? '/admin/dashboard'
    : userIsOwner
    ? '/owner/dashboard'
    : '/concierge/dashboard';

  // Clear tenant-view flag when user navigates back to their role route on their own
  useEffect(() => {
    if (isOnRoleRoute && inTenantView) {
      exitTenantView();
    }
  }, [isOnRoleRoute, inTenantView, exitTenantView]);

  useEffect(() => {
    if (!user || !hasRoleView) {
      setHasApartment(false);
      return;
    }

    // Cache result per user to avoid re-querying on every route change
    const cacheKey = `view_toggle_has_apt_${user.id}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached !== null) {
      setHasApartment(cached === 'true');
      return;
    }

    let cancelled = false;
    const check = async () => {
      // 1. Direct tenant apartment
      const { data: userData } = await supabase
        .from('users')
        .select('apartment_id')
        .eq('id', user.id)
        .single();

      if (cancelled) return;

      if (userData?.apartment_id) {
        sessionStorage.setItem(cacheKey, 'true');
        setHasApartment(true);
        return;
      }

      // Admins must have a direct apartment_id to access tenant view.
      // Skipping co-tenant / unit_owner checks for admins prevents accidental
      // access when they have legacy or incidental records in those tables.
      if (userIsAdmin) {
        sessionStorage.setItem(cacheKey, 'false');
        setHasApartment(false);
        return;
      }

      // 2. Co-tenant link (non-admins only)
      const { data: coTenant } = await supabase
        .from('user_apartments')
        .select('id')
        .eq('user_id', user.id)
        .limit(1)
        .maybeSingle();

      if (cancelled) return;
      if (coTenant) {
        sessionStorage.setItem(cacheKey, 'true');
        setHasApartment(true);
        return;
      }

      // 3. Owned apartments (for owners) — lets them browse their units as a tenant
      if (userIsOwner) {
        const { data: owned } = await supabase
          .from('unit_owners')
          .select('id')
          .eq('user_id', user.id)
          .limit(1)
          .maybeSingle();

        if (cancelled) return;
        if (owned) {
          sessionStorage.setItem(cacheKey, 'true');
          setHasApartment(true);
          return;
        }
      }

      // 4. Managed apartments (for concierges)
      if (userIsConcierge) {
        const { data: managed } = await supabase
          .from('concierge_assignments')
          .select('id')
          .eq('concierge_id', user.id)
          .limit(1)
          .maybeSingle();

        if (cancelled) return;
        if (managed) {
          sessionStorage.setItem(cacheKey, 'true');
          setHasApartment(true);
          return;
        }
      }

      if (!cancelled) {
        sessionStorage.setItem(cacheKey, 'false');
        setHasApartment(false);
      }
    };

    check();
    return () => { cancelled = true; };
  }, [user?.id, hasRoleView, userIsOwner, userIsConcierge]);

  if (!hasApartment) return null;

  const handleGoToTenantView = () => {
    enterTenantView();
    navigate('/user/dashboard');
  };

  const handleGoToRoleView = () => {
    exitTenantView();
    navigate(roleRoute);
  };

  // Safe-area-aware bottom offset (handles iPhone home indicator)
  const baseClass = `
    fixed right-4 z-50
    flex items-center gap-2 px-4 py-2.5
    text-white text-sm font-medium rounded-full shadow-lg
    transition-colors
  `;
  const bottomClass = 'bottom-[calc(1.5rem+env(safe-area-inset-bottom,0px))]';

  if (isOnRoleRoute) {
    return (
      <button
        onClick={handleGoToTenantView}
        className={`${baseClass} ${bottomClass} bg-blue-600 hover:bg-blue-700 active:bg-blue-800`}
        aria-label="Switch to tenant view"
      >
        <LayoutDashboard className="w-4 h-4" />
        Tenant View
      </button>
    );
  }

  // Only show back button on user routes if they explicitly switched there
  if (isOnUserRoute && inTenantView) {
    const label = userIsAdmin ? 'Admin View' : userIsOwner ? 'Owner View' : 'Concierge View';
    return (
      <button
        onClick={handleGoToRoleView}
        className={`${baseClass} ${bottomClass} bg-red-600 hover:bg-red-700 active:bg-red-800`}
        aria-label={`Switch to ${label.toLowerCase()}`}
      >
        <Shield className="w-4 h-4" />
        {viewingApartmentId ? `← ${label}` : label}
      </button>
    );
  }

  return null;
};

export default ViewToggle;
